import get from 'lodash/get';

import { ability } from '../components/Can';

/**
 * Parses a string and returns the corresponding value based on the string's content.
 * If the string is 'true', it returns true. parseConfig('true') // true
 * If the string is 'false', it returns false.
 * If the string is a valid number, it returns the parsed number.
 * Otherwise, it returns the original string.
 * @param {string} str - The string to be parsed.
 * @returns {boolean|number|string} - The parsed value based on the string's content.
 */
export const parseConfig = (str) => {
  if (typeof str === 'string') {
    if (str === 'true') return true;
    if (str === 'false') return false;
    if (!isNaN(str) && !isNaN(parseFloat(str))) return parseFloat(str);
    return str;
  }
  return str;
};

export const checkPrivilege = (privilege) => {
  const lastDotIndex = privilege.lastIndexOf('.');
  // As ability is used as a singleton, we call the function directly in here
  // This is a downside as we cannot memoize the wrapper function and we have to call the function every time
  const action = privilege.substring(lastDotIndex + 1);
  const subject = privilege.substring(0, lastDotIndex);
  const check = ability.can(action, subject);
  return check;
};

export const checkConfig = (config, configuration) => {
  const lastDotIndex = config.lastIndexOf('.');
  const configSetting = get(configuration, config.substring(0, lastDotIndex), false);
  const configValue = parseConfig(config.substring(lastDotIndex + 1));
  const check = configValue == configSetting;
  return check;
};

/**
 * Determines whether the component should be rendered based on the access control and configuration.
 * @param {Object} options - The options object.
 * @param {Array} options.accessControl - The access control conditions.
 * @param {Object} options.configuration - The configuration core object.
 * @returns {boolean} - Returns true if the component should be rendered, otherwise false.
 */

export const shouldRender = ({ accessControl, configuration }) => {
  let currentResult = true;
  let nextOperator = 'AND';
  // Check if accessControl is an array to continue
  if (!Array.isArray(accessControl) && accessControl != null) return false;
  // Use simple comparison if accessControl is not defined
  if (accessControl == null) return true;

  for (const condition of accessControl) {
    if (condition === 'AND' || condition === 'OR') {
      nextOperator = condition;
      continue;
    }

    let conditionResult;

    const firstDotIndex = condition.indexOf('.');

    // Here we add a config provider and a privilege provider, we can further extend this to add more providers
    if (condition.startsWith('configuration.')) {
      conditionResult = checkConfig(condition.substring(firstDotIndex + 1), configuration);
    } else if (condition.startsWith('privilege.')) {
      conditionResult = checkPrivilege(condition.substring(firstDotIndex + 1));
    } else {
      return false; // Unknown condition
    }

    if (nextOperator === 'AND') {
      currentResult = currentResult && conditionResult;
    } else if (nextOperator === 'OR') {
      currentResult = currentResult || conditionResult;
    }

    nextOperator = 'AND';
  }

  return currentResult;
};
